import React from 'react';
import { Box } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { Link } from 'react-router-dom';
import { ONE_STOP_PATH } from 'domain/shared/oneStop';
import Logo from 'assets/images/logo-white.svg';
import LogoSmall from 'assets/images/logo-icon.svg';
import { styled } from '@mui/material/styles';
import { Tooltip } from 'framework/ui/mui-builder';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import IconButton from '@mui/material/IconButton';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { HeaderMenu } from 'framework/ui/menu';
import { SidebarItem, useSidebarContext } from 'framework/ui/layout';

export const DRAWER_WIDTH = '226px';
export const COLLAPSED_WIDTH = '64px';
const STATUS_PAGE = 'https://syberry-corporation.github.io/status-page/dashboard';

const StyledDrawer = styled(Drawer)(({ theme }: { theme: any }) => ({
  transition: 'width 0.25s ease',
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    border: 0,
    width: DRAWER_WIDTH,
    background: theme.vars.palette.common.sidebarMenuBg,
    position: 'fixed',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'initial',
    padding: '20px 0px',
    minHeight: 'calc(100vh - 25px)',
    transition: 'width 0.25s ease',

    [theme.breakpoints.between('lg', 'xxxl')]: {
      padding: '12px 0px',
    },

    // Inner container with scroll
    '& .drawer-scroll-area': {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
  },

  // Common styles for list items
  '& .MuiListItemIcon-root': {
    color: theme.vars.palette.common.sidebarMenuText,
    opacity: 0.65,
    minWidth: '31px',
    '& .MuiSvgIcon-root': {
      width: '0.85em',
      height: '0.85em',
    },
  },
  '& .MuiListItemButton-root': {
    padding: '6px 6px 6px 4px',
    '&:hover': {
      background: theme.vars.palette.primary.main,
    },
    '&.active': {
      backgroundColor: theme.vars.palette.common.sidebarMenuActive,
    },
  },
  '& .MuiListItemText-primary': {
    lineHeight: '1.45',
    color: theme.vars.palette.common.sidebarMenuText,
  },
  '& .MuiDivider-root': {
    border: 0,
    margin: '12px 0',
  },
  '& .MuiCollapse-root': {
    '& .MuiListItemIcon-root': { '&:empty': { display: 'none' } },
    '& .MuiListItem-root': {
      position: 'relative',
      paddingLeft: '29px',
      marginTop: 0,
      '&::after': {
        content: '""',
        position: 'absolute',
        width: '2px',
        height: '100%',
        background: theme.vars.palette.common.sidebarMenuText,
        opacity: 0.35,
        left: '16px',
        top: '0',
      },
      '& + .MuiCollapse-root': {
        marginBottom: 0,
        '& .MuiListItem-root': {
          paddingLeft: '55px',
        },
      },
    },
    '& .MuiListItemButton-root': {
      paddingLeft: '10px',
    },
  },
  '& .MuiSvgIcon-root': {
    color: theme.vars.palette.common.sidebarMenuText,
    opacity: 0.65,
  },
}));

interface AppSidebarNavProps {
  navConfig: any;
}

const AppSidebarNav = (props: AppSidebarNavProps) => {
  const {
    navConfig: { sidebarItems = [], headerMenuData = [], clickHandler },
  } = props as any;
  const { isFeatureEnabled } = useFeatureToggle();
  const { isDesktop, mobileOpen = false, handleDrawerToggle = () => {}, collapsed, setCollapsed } = useSidebarContext();

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed!(next);
  };

  const drawer = (
    <>
      <Box
        sx={{
          padding: '7px 10px 0px 10px',
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',

          justifyContent: (!collapsed && isDesktop) || !isDesktop ? 'space-between' : 'center',
        }}
      >
        <Link to={ONE_STOP_PATH}>
          <img
            src={(!collapsed && isDesktop) || !isDesktop ? Logo : LogoSmall}
            alt="Syberry logo"
            width={(!collapsed && isDesktop) || !isDesktop ? 103 : 32}
          />
        </Link>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {((!collapsed && isDesktop) || !isDesktop) && isFeatureEnabled(Features.SYSTEMS_HEALTH) ? (
            <Tooltip arrow placement="bottom" title="Systems health check">
              <Link style={{ marginRight: '10px' }} target="_blank" to={STATUS_PAGE}>
                <i style={{ fontSize: '20px' }} className="fa fa-heartbeat" aria-hidden />
              </Link>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <List>
        {sidebarItems?.map((item: any) => (
          <SidebarItem key={`${item.url}-${item.name}`} {...item} clickHandler={clickHandler} />
        ))}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        position: 'relative',
        width: { lg: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH },
        flexShrink: { lg: 0 },
        zIndex: { xs: 10000, lg: 1200 },
        transition: 'width 0.25s ease',
      }}
      aria-label="mailbox folders"
    >
      {!isDesktop ? (
        <StyledDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              overflow: 'auto',
            },
          }}
        >
          {drawer}
        </StyledDrawer>
      ) : (
        <>
          <HeaderMenu menuData={headerMenuData} />

          <StyledDrawer
            variant="permanent"
            open
            sx={{
              height: '100%',
              '& .MuiDrawer-paper': {
                width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
              },
            }}
          >
            <Box className="drawer-scroll-area">{drawer}</Box>

            <Tooltip arrow placement="bottom" title={collapsed ? 'Expand menu' : 'Collapse menu'}>
              <IconButton
                onClick={toggleCollapsed}
                size="small"
                sx={{
                  position: 'absolute',
                  right: '-17px',
                  top: '13px',
                  zIndex: 1301,
                  backgroundColor: 'var(--mui-palette-common-sidebarMenuBg)',
                  transition: 'transform 0.3s ease, background-color 0.3s ease',
                  transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                  '&:hover': {
                    backgroundColor: 'var(--mui-palette-primary-main)',
                  },
                }}
              >
                <PlayCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </StyledDrawer>
        </>
      )}
    </Box>
  );
};

export default AppSidebarNav;
